import * as THREE from 'three';
import { APP_VERSION } from '@/version';
import { EventBus } from '@/core/EventBus';
import type { AppEvents } from './AppEvents';
import { WebGLRendererAdapter, type Renderer } from '@/rendering/Renderer';
import { RenderQuality } from '@/rendering/RenderQuality';
import { SceneManager } from '@/rendering/SceneManager';
import { CameraManager, CameraViewMode } from '@/rendering/CameraManager';
import { CameraController } from '@/rendering/CameraController';
import { LightingManager } from '@/rendering/LightingManager';
import { SkyEnvironment } from '@/rendering/SkyEnvironment';
import { exemptOverlays, installDepthGrade } from '@/rendering/DepthGrade';
import { Terrain } from '@/terrain/Terrain';
import { createGenerationParams } from '@/terrain/TerrainTemplates';
import type { TerrainGenerationParams, TerrainProjectState } from '@/terrain/TerrainTypes';
import { createNorthIndicator, disposeHelper } from '@/terrain/TerrainGrid';
import { GridOverlay } from '@/editor/GridOverlay';
import { ContourOverlay, type ContourLabelMode } from '@/editor/ContourOverlay';
import { deserializeSurfaceMap, type SurfaceRect } from '@/terrain/TerrainSurface';
import { vertexToWorld } from '@/terrain/TerrainSculpt';
import { loadDefaultCatalog } from '@/flora/FloraCatalog';
import { FloraEditor } from '@/editor/FloraEditor';
import { TerrainEditor, type TerrainToolKind } from '@/editor/TerrainEditor';
import { WandTool } from '@/editor/WandTool';
import { WandBubble } from '@/ui/WandBubble';
import { TagTool } from '@/editor/TagTool';
import { TagBubble } from '@/ui/TagBubble';
import { AddTagCommand, RemoveTagsCommand, UpdateTagCommand } from '@/editor/TagCommands';
import { TagStore } from '@/semantics/TagStore';
import { cloneTag, taggedEntityId, type SemanticTag, type TagTarget } from '@/semantics/SemanticTag';
import type { TagImage } from '@/semantics/TagImage';
import {
  floraSemantics,
  groundSemantics,
  structureSemantics,
  type SemanticProfile,
} from '@/semantics/SemanticProfile';
import {
  findSurfaceLayer,
  surfacePalette,
  type SurfaceLayer,
  type SurfacePalette,
} from '@/terrain/TerrainSurface';
import { TextureStudio } from '@/ui/TextureStudio';
import { PaintStrokeCommand } from '@/editor/TerrainCommands';
import { ApiKeyStore } from '@/capture/ApiKeyStore';
import { useProxiedAi } from '@/capture/AiGateway';
import { PLANS } from '@shared/plans';
import {
  CloudPhotoStore,
  CloudProjectStore,
  CloudSession,
  licenseFor,
  type SessionState,
} from '@/cloud';
import { AccountMenu, describeCredential } from '@/ui/AccountMenu';
import { applyWandSpell, emptyWandReport, type WandReport, type WandSpell } from '@/wand';
import { SiteObjectWorld } from '@/site/SiteObjectWorld';
import { FaunaWorld } from '@/fauna/FaunaWorld';
import { FaunaFactory } from '@/fauna/FaunaFactory';
import { createFaunaInstance, defaultHeightOffset } from '@/fauna/FaunaInstance';
import { defaultPose, poseAttachments } from '@/fauna/FaunaDefinition';
import { AddFaunaCommand } from '@/editor/FaunaCommands';
import { StaffageTool } from '@/editor/StaffageTool';
import { createFaunaLayer, createPropLayer } from '@/editor/StaffageLayer';
import { groupSizeFor, headingFor, scatterStaffage } from '@/editor/StaffagePlacement';
import { propCatalog } from '@/site/PropCatalog';
import { FaunaCatalog, loadDefaultFaunaCatalog } from '@/fauna/FaunaCatalog';
import type { SiteObject } from '@/site/SiteObject';
import { propLibrary } from '@/site/PropLibrary';
import type { PropDefinition } from '@/site/PropCatalog';
import { AddSiteObjectsCommand } from '@/editor/SiteObjectCommands';
import { EnvironmentManager } from '@/environment/EnvironmentManager';
import { FloraResolver } from '@/localization/FloraResolver';
import {
  defaultLocalization,
  type SiteConditions,
  type SiteConditionsPatch,
  type SiteLocalization,
} from '@/localization/SiteLocalization';
import { getTerrainTemplate } from '@/terrain/TerrainTemplates';
import { Hud, type CameraViewName } from '@/ui/Hud';
import { PhotoMode } from '@/ui/PhotoMode';
import { QuickRender } from '@/ui/QuickRender';
import { audienceFor } from '@/ui/Tour';
import { tourController, type TourController } from '@/ui/TourOverlay';
import { DetailToggle } from '@/ui/DetailToggle';
import { LensControl } from '@/ui/LensControl';
import { ShotsPanel } from '@/ui/ShotsPanel';
import { PlantManifestPanel } from '@/ui/PlantManifestPanel';
import { TagIndexPanel } from '@/ui/TagIndexPanel';
import { SiteLensBar } from '@/ui/SiteLensBar';
import { SiteLensRenderer, type LensTarget } from '@/rendering/SiteLensRenderer';
import {
  readLens,
  summarizeLens,
  type LensDefinition,
  type LensReading,
  type LensSummary,
} from '@/ui/FloraLens';

/**
 * The reading for a plant that has gone from under the map — deleted while the plan was open, or
 * undone. Unrecorded rather than zero, for the same reason an unassessed record is: the lens has not
 * answered "none" about this plant, it has failed to find it.
 */
const UNREADABLE_LENS: LensReading = {
  value: 0,
  peakValue: 0,
  peakDay: 1,
  contributions: [],
  described: false,
};
import { ProjectMenu } from '@/ui/ProjectMenu';
import { CaptureService } from '@/capture/CaptureService';
import type { StoredShot } from '@/capture/PhotoStorage';
import {
  defaultEnvironmentState,
  deserializeEnvironmentState,
} from '@/environment/EnvironmentState';
import {
  DEFAULT_PROJECT_NAME,
  PROJECT_SCHEMA_VERSION,
  deriveAliasGroups,
  newProjectIdentity,
  tagTargetExists,
  type LandscapeProject,
  type ProjectIdentity,
  type ProjectLoadReport,
} from '@/project/Project';
import { serializeProject } from '@/project/ProjectSerializer';
import { generatesContent, newSiteParams, type NewSiteOptions } from '@/project/NewSite';
import { residentialLotArtifacts } from '@/project/ResidentialLot';
import { compilePipeline } from '@/reference';
import type { UnitSystem } from '@/core/Units';

export interface ApplicationOptions {
  container: HTMLElement;
  quality?: RenderQuality;
  /** Injected renderer (tests supply a mock to avoid a real WebGL context). */
  renderer?: Renderer;
  /**
   * The hosted-tier session, already probed.
   *
   * Supplied by `bootstrapGated`, which has to ask the server who the user is *before* deciding
   * whether to construct this class at all. Passing the answer in means the probe happens once;
   * omit it and the app makes its own, which is what the standalone build does.
   */
  session?: CloudSession;
  /**
   * Something covering the screen that has to come off before the tutorial may open — the opening
   * film, in the shipped build.
   *
   * Passed in rather than discovered because the splash is deliberately not the application's
   * business: it mounts on `<body>` before this class exists and dissolves on its own clock. The
   * one thing the two have to agree about is that a first-run tutorial must not play out under a
   * curtain, so the curtain hands over a promise and nothing else is coupled. Omitted, the tour is
   * offered as soon as the session resolves.
   */
  openingCurtain?: Promise<unknown>;
}

/**
 * Composes the application lifecycle: renderer, scene, camera, lighting, input, and a fixed-step
 * update + render loop with clean teardown (spec §5, milestone 1.5).
 */
export class Application {
  readonly events = new EventBus<AppEvents>();
  readonly scene: SceneManager;
  readonly camera: CameraManager;
  readonly lighting: LightingManager;
  /** Background, image-based fill and fog — all driven by the sun. See {@link SkyEnvironment}. */
  readonly sky: SkyEnvironment;
  readonly environment: EnvironmentManager;
  readonly renderer: Renderer;
  readonly capture: CaptureService;
  /** Buildings and paving. Owned here rather than by the flora editor — they are not planting. */
  readonly siteObjects: SiteObjectWorld;
  /**
   * Creatures and figures. Owned here for the same reason site objects are: staffage is not planting,
   * and the flora editor's selection, growth and season machinery has nothing to say about a rabbit.
   */
  readonly fauna: FaunaWorld;
  /** The catalog behind {@link fauna} — read by the palette and by placement. */
  readonly faunaCatalog: FaunaCatalog = loadDefaultFaunaCatalog();
  /**
   * What the site *means*. Owned at the application level rather than by any one world, because a tag
   * can be about a plant, a building, a material, a place, or the garden as a whole, and none of
   * those owners can hold the others' tags.
   */
  readonly tags = new TagStore();
  /**
   * Every localized fact about every plant on this site, derived on demand.
   *
   * Owned here for the same reason the tags are: suitability belongs to no single world. The palette
   * asks it about species it has never planted, the canvas asks it about specimens, the manifest asks
   * it about the site as a whole. One resolver means those three can never disagree — see
   * `localization/FloraResolver.ts`.
   */
  readonly flora: FloraResolver;

  private readonly container: HTMLElement;
  /**
   * Where this site is, or undefined when nobody has said.
   *
   * Undefined is a real state rather than a missing one: an unlocalized site draws no suitability
   * badges and raises no warnings, because nothing has been checked. Setting it never rewrites a
   * flora record — it clears the resolver's cache and everything is derived again. See
   * {@link localizeSite}.
   */
  private siteLocalization: SiteLocalization | undefined;
  private readonly cameraController: CameraController | null = null;
  private floraEditor: FloraEditor | null = null;
  private terrainEditor: TerrainEditor | null = null;
  private wandTool: WandTool | null = null;
  private wandBubble: WandBubble | null = null;
  private wandBusy = false;
  private tagTool: TagTool | null = null;
  private tagBubble: TagBubble | null = null;
  private readonly apiKeys = new ApiKeyStore();
  /**
   * The hosted tier, if there is one.
   *
   * Constructed unconditionally but inert until {@link connectSession} finds a server: with none
   * reachable the session resolves to local mode, `cloudProjects` stays null, and every store in
   * the app is the IndexedDB one it has always been. That is what keeps `npm run dev` against a
   * bare Vite server — and a `dist/` opened from disk — working exactly as before.
   */
  private readonly session: CloudSession;
  private cloudProjects: CloudProjectStore | null = null;
  private cloudPhotos: CloudPhotoStore | null = null;
  private accountMenu: AccountMenu | null = null;
  /**
   * The first-run tutorial. Held here rather than in the HUD because who has seen it is a fact
   * about the *account*, and the account is this class's business — see {@link offerTour}.
   */
  private readonly tour: TourController = tourController();
  /** Whether the tour has already been offered this session, per audience. Guards the re-offer. */
  private readonly tourOffered = new Set<string>();
  /** What has to come off the screen before the tutorial may open. See {@link ApplicationOptions}. */
  private readonly openingCurtain: Promise<unknown> | null;
  private textureStudio: TextureStudio | null = null;
  private hud: Hud | null = null;
  /** Click-the-palette, click-the-ground placement for fauna and for objects. */
  private faunaTool: StaffageTool | null = null;
  private objectTool: StaffageTool | null = null;
  /** Pose armed alongside the fauna record, chosen in the palette. */
  private armedFaunaPose: string | null = null;
  /** Builds ghost previews for the fauna tool. Shares nothing with the world's own factory. */
  private readonly ghostFactory = new FaunaFactory();
  private photoMode: PhotoMode | null = null;
  /**
   * The full-sheet shots browser, over the designer. Built once and kept across a storage handover:
   * it reads the store through a getter, so signing in swaps the library under it without a rebuild.
   */
  private shotsPanel: ShotsPanel | null = null;
  private quickRender: QuickRender | null = null;
  /** The lens row beside the render button — the only thing that changes the camera's field of view. */
  private lensControl: LensControl | null = null;
  /** The surface-detail switch, left of the lens. See `DetailShading.ts`. */
  private detailToggle: DetailToggle | null = null;
  private plantManifest: PlantManifestPanel | null = null;
  /** The whole document's semantic tags, read as a sheet. See `TagIndexPanel.ts`. */
  private tagIndex: TagIndexPanel | null = null;
  /** The lens bar at the foot of the view, and what it paints onto the scene. See `FloraLens.ts`. */
  private lensBar: SiteLensBar | null = null;
  private lensRenderer: SiteLensRenderer | null = null;
  /** The last pass's readings, so the bar's caption does not re-read the whole garden to count it. */
  private readonly lensReadings = new Map<string, LensReading>();
  private projectMenu: ProjectMenu | null = null;
  private readout: HTMLElement | null = null;
  terrain: Terrain | null = null;
  /**
   * The reference grid. Owned across terrain loads rather than rebuilt with them, so switching it on
   * and then opening a project does not silently switch it back off.
   */
  private gridOverlay: GridOverlay | null = null;
  /**
   * The topographic map. Owned across terrain loads for the same reason the grid is — it is a way of
   * looking at the site, not a part of one.
   */
  private contourOverlay: ContourOverlay | null = null;
  /**
   * What the topo overlay was set to before sculpt mode switched it on, or null when sculpt did not
   * switch it on. See {@link topoSculptTransition}.
   */
  private topoBeforeSculpt: boolean | null = null;
  private northIndicator: THREE.Group | null = null;

  private smoothedFps = 0;

  private readonly fixedStep = 1 / 60;
  private accumulator = 0;
  private lastTime = 0;
  private elapsed = 0;
  private rafId: number | null = null;
  private running = false;

  private readonly resizeObserver: ResizeObserver | null = null;
  private readonly unsubscribe: (() => void)[] = [];
  private readonly onKeyDown = (e: KeyboardEvent): void => this.handleKey(e);

  constructor(options: ApplicationOptions) {
    // Before anything that could compile a material — the grade is spliced into three's own fog
    // chunk, and a program built from the stock chunk is cached with the stock chunk in it. First
    // statement in the constructor for that reason, ahead even of the renderer.
    installDepthGrade();

    this.container = options.container;
    this.session = options.session ?? new CloudSession();
    this.openingCurtain = options.openingCurtain ?? null;
    const quality = options.quality ?? RenderQuality.Medium;

    // Reads the profile through a closure rather than holding a copy, so re-localizing the site is a
    // single assignment plus a cache clear — there is no second place for a stale profile to live.
    this.flora = new FloraResolver({
      localization: () => this.siteLocalization ?? defaultLocalization(),
      catalogVersion: () => this.floraEditor?.catalog.version ?? '0',
    });

    this.renderer = options.renderer ?? new WebGLRendererAdapter({ quality });
    this.scene = new SceneManager();
    this.camera = new CameraManager({ aspect: this.aspect() });
    this.lighting = new LightingManager(quality);
    this.scene.environmentRoot.add(this.lighting.root);
    this.sky = new SkyEnvironment(this.scene.scene, this.renderer);

    this.environment = new EnvironmentManager({
      maxSunIntensity: 2.2,
      onSunChange: (l) => {
        // Position the sun along the direction toward it (negation of the light travel direction).
        this.lighting.setSunDirection(new THREE.Vector3(-l.direction.x, -l.direction.y, -l.direction.z));
        this.lighting.setSunIntensity(l.intensity);
        this.lighting.setAmbientIntensity(l.ambientIntensity);
        // The sky is lit by the same sun, so it moves here rather than on its own schedule — that
        // is what keeps the fog, the background and the fill from disagreeing about the hour.
        this.sky.update({ elevationDegrees: l.elevationDegrees });
      },
      onSeasonChange: (dayOfYear) => {
        this.floraEditor?.world.applySeason(dayOfYear);
        // The lens follows the date: scrubbing the year is what turns the heat map into an argument
        // about seasonal continuity rather than a still picture of high summer.
        this.applyLens();
      },
      // Snow and ice are a property of the ground, so they go to the terrain rather than to the
      // lighting — and they arrive here on a date change as well as on a switch, because dragging
      // out of the freezing window is what takes the snow off.
      onGroundCoverChange: (cover) => {
        this.terrain?.setWinterCover(cover);
      },
      // Sun, season and weather are all saved with the site, so moving any of them is an edit.
      onChange: () => this.projectMenu?.markDirty(),
    });

    this.siteObjects = new SiteObjectWorld(this.scene.siteRoot);
    // Into the site root beside the buildings rather than the flora root: a placed creature is not
    // affected by the season model, and `FloraWorld.applySeason` walks everything under its own root.
    this.fauna = new FaunaWorld(this.faunaCatalog, this.scene.siteRoot);

    this.capture = new CaptureService({
      renderer: this.renderer,
      scene: this.scene,
      camera: this.camera,
      environment: this.environment,
      world: () => this.floraEditor?.world ?? null,
      // So an AI render is told about the plants the frame actually shows, resolved for this site.
      resolver: () => this.flora,
      siteObjects: () => this.siteObjects,
      // So an AI render is told about the creatures and figures in the frame, instead of being shown
      // an unexplained low-poly mass inside a prompt that forbids adding anything.
      fauna: () => this.fauna,
      tags: () => this.tags,
      terrain: () => this.terrain,
      // Stamped into the manifest so a shot can be recognised as belonging to this site long after
      // the session that took it — which is what makes re-staging one safe to offer.
      siteIdentity: () => this.siteIdentity(),
      viewport: () => this.size(),
    });

    this.loadTerrain(createGenerationParams('rolling-landscape'));

    // Only wire DOM-dependent pieces when a real canvas/container is present.
    if (typeof document !== 'undefined' && this.renderer.domElement) {
      this.container.appendChild(this.renderer.domElement);
      this.cameraController = new CameraController(this.renderer.domElement, this.camera, {
        // Resolved at call time: the HUD that draws the pointer is built some way below this, and
        // navigation is live from the moment the canvas is in the page.
        onNavAction: (action) => this.hud?.setNavigating(action),
      });
      window.addEventListener('keydown', this.onKeyDown);
      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(() => this.resize());
        this.resizeObserver.observe(this.container);
      }
      this.floraEditor = new FloraEditor({
        domElement: this.renderer.domElement,
        getCamera: () => this.camera.activeCamera,
        floraRoot: this.scene.floraRoot,
        overlayRoot: this.scene.overlayRoot,
        terrain: () => this.terrain,
        catalog: loadDefaultCatalog(),
        resolver: this.flora,
        siteObjects: () => this.siteObjects.all(),
        // Creatures and props join the plants in one selection, one history and one Delete key.
        // Buildings and paving deliberately do not — see `createPropLayer`.
        staffage: [createFaunaLayer(this.fauna), createPropLayer(this.siteObjects)],
        staffageRoot: this.scene.siteRoot,
        readout: this.createReadout(),
        // Resolved at call time, because the HUD that draws these is built a few lines below the
        // editor that raises them.
        onNotice: (text) => this.hud?.noteAtCursor(text),
        onFocusSelection: (ids) => this.focusSelection(ids),
        onSetView: (view) => this.setView(view === 'front' ? 'presentation' : view),
        onTogglePhotoMode: () => this.togglePhotoMode(),
        onSave: () => this.projectMenu?.save(),
        onSaveAs: () => this.projectMenu?.openSaveAs(),
        onRename: () => this.projectMenu?.openRename(),
        onPaintTool: () => this.toggleGroundTool('paint'),
        onSculptTool: () => this.toggleGroundTool('sculpt'),
        onWandTool: () => this.setWandTool(!(this.wandTool?.isActive ?? false)),
        onTagTool: () => this.setTagTool(!(this.tagTool?.isActive ?? false)),
        onToggleGrid: () => this.setGridVisible(),
        onToggleTopo: () => this.setTopoVisible(),
        // Same arrangement as the history panel below: the manifest owns the callouts, the editor
        // only owns the key, and it is resolved at press time because the panel is built later.
        onTogglePointers: () => this.plantManifest?.togglePointers(),
        // The HUD owns the panel; the editor only owns the key that asks for it. Resolved at press
        // time, because the HUD is built a few lines below this editor.
        onToggleHistory: () => this.hud?.toggleHistory(),
        onRequestCanvas: () => {
          this.setGroundTool(null);
          this.setWandTool(false);
          this.setTagTool(false);
          this.cancelStaffageTools();
        },
      });
      // Apply the current season to any freshly created flora views.
      this.floraEditor.world.applySeason(this.environment.effectiveDayOfYear());

      // The ground tools share the flora editor's history, so Ctrl+Z walks one timeline across the
      // whole site rather than two that interleave unpredictably.
      this.terrainEditor = new TerrainEditor({
        domElement: this.renderer.domElement,
        getCamera: () => this.camera.activeCamera,
        terrain: () => this.terrain,
        history: this.floraEditor.history,
        onGroundChanged: (rect) => this.handleGroundChanged(rect),
        onChange: () => this.hud?.requestRefresh(),
        onExit: () => this.setGroundTool(null),
        // The brush's power commands are handled in the editor but taught by the HUD, so the HUD
        // has to hear about the first use of one to stop teaching it.
        onAdvancedCommand: () => this.hud?.noteGroundShortcut(),
        ...(this.readout ? { readout: this.readout } : {}),
      });
      this.scene.overlayRoot.add(this.terrainEditor.overlayObject);

      this.wandTool = this.createWandTool(this.floraEditor);
      this.scene.overlayRoot.add(this.wandTool.overlayObject);

      this.tagTool = this.createTagTool(this.floraEditor);
      this.scene.overlayRoot.add(this.tagTool.overlayObject);

      this.faunaTool = this.createFaunaTool();
      this.scene.overlayRoot.add(this.faunaTool.overlayObject);

      this.objectTool = this.createObjectTool();
      this.scene.overlayRoot.add(this.objectTool.overlayObject);

      this.hud = this.createHud(this.floraEditor);
      this.mountAccountMenu(this.hud);
      this.wandBubble = this.createWandBubble(this.hud, this.floraEditor);
      this.tagBubble = this.createTagBubble(this.hud, this.floraEditor);
      this.photoMode = this.createPhotoMode();
      // Photo mode first: the quick renderer files what it keeps in that mode's store, so the two
      // paths write to one gallery rather than to two that disagree. The shots browser reads that
      // same store, for the same reason.
      this.quickRender = this.createQuickRender();
      // Left of Render, in the same corner row: the lens decides what that button will be sent, so
      // the two belong side by side rather than in different halves of the screen.
      this.lensControl = new LensControl({
        onChange: (zoom) => this.camera.setLensZoom(zoom),
        current: () => this.camera.lensZoom,
      });
      // Furthest left of the three, because it is the one that is set once and then left alone: the
      // grain is a property of how the garden is drawn, where the lens is a decision retaken for
      // every frame. Nothing to drive — the switch reaches every material in the scene through one
      // shared uniform, and the render loop is already running.
      this.detailToggle = new DetailToggle();
      this.quickRender.mountLeading(this.detailToggle.root);
      this.quickRender.mountLeading(this.lensControl.root);
      this.hud.mountViewportControl(this.quickRender.root);

      this.shotsPanel = new ShotsPanel({
        store: () => this.photoMode?.store ?? null,
        currentSite: () => {
          const identity = this.siteIdentity();
          return identity ?? { id: null, name: this.projectMenu?.documentName ?? DEFAULT_PROJECT_NAME };
        },
        onRestage: (shot) => this.restageShot(shot),
        onOpenChange: (open) => this.hud?.setShotsOpen(open),
      });
      this.hud.mountOverlay(this.shotsPanel.root);

      // Opposite corner of the same column: the render button acts on the view, the manifest
      // reports on what is in it. Both belong to the canvas rather than to a tray.
      this.plantManifest = new PlantManifestPanel({
        editor: this.floraEditor,
        preferences: this.hud.preferences,
        site: () =>
          this.terrain
            ? {
                widthMeters: this.terrain.widthMeters,
                lengthMeters: this.terrain.lengthMeters,
                templateName: getTerrainTemplate(this.terrain.params.templateId).name,
              }
            : null,
        siteObjects: () => this.siteObjects.all(),
        onFocus: (ids) => this.focusSelection(ids),
        resolver: () => this.flora,
        localization: () => this.localization,
        // The camera and the canvas it draws into, which together are what let the manifest print
        // itself onto the garden as callout headers. Resolved per call: the top view swaps the
        // active camera for an orthographic one, and the pointers have to follow it.
        camera: () => this.camera.activeCamera,
        canvas: () => this.renderer.domElement ?? null,
        // So the plan map can be dressed in whatever lens the design view is reading. The reading is
        // handed over rather than recomputed there, which is what makes the plan and the view the
        // same numbers instead of two implementations that agree most of the time.
        lensReading: (plant, lens) => this.lensReadingFor(plant.id, lens),
      });
      this.hud.mountViewportControl(this.plantManifest.root);

      // The second reading of the same document: what the site has been *told*, as against what is
      // planted in it. It shares the manifest's corner row rather than claiming one of its own,
      // because both are the act of reading the site rather than working on it — and because the
      // tags are otherwise invisible, which is the whole reason this sheet exists.
      this.tagIndex = new TagIndexPanel({
        tags: this.tags,
        describeTarget: (target) => this.describeTagTarget(target, this.floraEditor),
        unit: () => this.hud?.preferences.unit ?? 'meters',
        onReveal: (tag) => this.revealTag(tag),
      });
      this.plantManifest.mountTrailing(this.tagIndex.bar);
      this.hud.mountViewportControl(this.tagIndex.root);

      // The lens bar stands on the bottom edge of the same column, under everything else in it. Its
      // renderer goes into the overlay root beside the selection halos and the grid, which is also
      // what gets it hidden for free on every capture pass.
      this.lensRenderer = new SiteLensRenderer((id) => this.floraEditor?.world.view(id));
      this.scene.overlayRoot.add(this.lensRenderer.object);
      this.lensBar = new SiteLensBar({
        preferences: this.hud.preferences,
        onLensChange: () => {
          this.applyLens();
          // The plan map opened from this bar is the same lens seen from above, so a chip picked —
          // or dropped, or read at peak instead of today — re-dresses it where it stands. Hung off
          // the bar's own signal rather than off `applyLens`, which also runs on every edit and
          // would redraw the plan a second time behind the panel's own coalesced refresh.
          this.plantManifest?.syncMapLens(this.lensBar?.lens ?? null);
        },
        onOpenMap: () => this.plantManifest?.openMap(this.lensBar?.lens ?? null),
        summary: () => this.lensSummary(),
      });
      this.hud.mountViewportControl(this.lensBar.root);
      // A lens is a preference, so it survives a reload — and the garden has to come back wearing it.
      this.applyLens();

      this.projectMenu = this.createProjectMenu(this.hud);

      // The contour interval and its figures are chosen in whatever unit the rest of the HUD is
      // reading in, so switching to feet redraws the map at whole-foot spacing rather than
      // relettering metric lines with awkward conversions.
      const preferences = this.hud.preferences;
      const syncTopoUnit = (): void => {
        this.contourOverlay?.setUnit(preferences.unit === 'feet' ? 'imperial' : 'metric');
      };
      syncTopoUnit();
      this.unsubscribe.push(preferences.onChange(syncTopoUnit));

      // Plants, groups, alias links and ground strokes all report here; the environment reports
      // through its own `onChange` above. Together they are every way the document can change.
      this.unsubscribe.push(
        this.floraEditor.onDocumentChange(() => {
          this.projectMenu?.markDirty();
          // Planting, deleting, ageing and undoing all move the reading — and ageing in particular
          // rebuilds the view, which drops the glow the renderer put on the old materials.
          this.applyLens();
        }),
      );
      // Tags are part of the document — they change what a render produces — so writing one is an
      // edit, and the bubble is redrawn from the store rather than from its own copy so an undo
      // taken with the panel open is visible in it.
      this.unsubscribe.push(
        this.tags.onChange(() => {
          this.projectMenu?.markDirty();
          this.tagBubble?.refresh();
          // The index is the same store read as a document, so it invalidates on the same beat —
          // including on a project load, which replaces every tag at once.
          this.tagIndex?.invalidate();
          this.hud?.requestRefresh();
        }),
      );
    }
    this.resize();
  }

  /** Start the render loop. */
  start(): void {
    if (this.running) return;
    this.running = true;
    this.lastTime = this.now();
    // Deliberately not awaited: the editor must paint and be usable while the session probe is in
    // flight. Until it resolves the app is in local mode, which is a fully working state rather
    // than a loading one, so there is nothing to block on.
    void this.connectSession();
    this.events.emit('app:ready', undefined);
    if (typeof requestAnimationFrame !== 'undefined') {
      const tick = (): void => {
        if (!this.running) return;
        this.frame();
        this.rafId = requestAnimationFrame(tick);
      };
      this.rafId = requestAnimationFrame(tick);
    }
  }

  /** Advance one frame: fixed-step update(s) then render. Exposed for tests. */
  frame(): void {
    const time = this.now();
    let delta = (time - this.lastTime) / 1000;
    this.lastTime = time;
    // Guard against long pauses (tab switch) producing a spiral of death.
    delta = Math.min(delta, 0.25);
    this.accumulator += delta;
    while (this.accumulator >= this.fixedStep) {
      this.update(this.fixedStep);
      this.accumulator -= this.fixedStep;
    }
    this.render(delta);
  }

  private update(dt: number): void {
    this.elapsed += dt;
    this.events.emit('render:frame', { deltaSeconds: dt, elapsedSeconds: this.elapsed });
  }

  private render(delta: number): void {
    if (delta > 0) {
      // Exponential smoothing keeps the status-bar readout legible instead of flickering.
      const instant = 1 / delta;
      this.smoothedFps = this.smoothedFps === 0 ? instant : this.smoothedFps * 0.9 + instant * 0.1;
    }
    // Brush strokes advance on the frame clock, not on pointer events — see `TerrainBrush`. Driving
    // it here is also what batches a frame's dabs into a single geometry or texture upload.
    this.terrainEditor?.tick(delta);
    // The planting brush scatters on the same clock and for the same reason: a drift's density has
    // to depend on how far the mouse moved, not on how many events the browser chose to send.
    this.floraEditor?.tick(delta);
    // The wand's ground ring re-seats itself on sculpted ground and pulses while a request is out.
    this.wandTool?.tick(delta, this.wandBusy);
    // The tag overlay rebuilds itself here rather than on every store event, so a burst of edits
    // costs one rebuild.
    this.tagTool?.tick();
    // Re-traces ground that moved, and fades the elevation figures by how big they now are on
    // screen — which is why it needs the live camera and not just the clock.
    this.contourOverlay?.tick(delta, this.camera.activeCamera);
    // The callouts re-solve their screen layout here rather than on document changes: a label has to
    // follow its plant on every frame the *camera* moves, which is most of them. Costs a matrix
    // comparison and nothing else while the view is parked.
    this.plantManifest?.tickPointers();
    this.floraEditor?.refresh();
    // Keeps the depth grade off the overlays. Here rather than at each tool's material because the
    // overlay root fills lazily from a dozen of them — see `exemptOverlays`. A traversal of a few
    // dozen objects, and free after the first pass over any given material.
    exemptOverlays(this.scene.overlayRoot);
    this.renderer.render(this.scene.scene, this.camera.activeCamera);
  }

  resize(): void {
    const { width, height } = this.size();
    this.renderer.setSize(width, height);
    this.camera.setAspect(width / Math.max(1, height));
    this.events.emit('renderer:resize', { width, height });
  }

  private handleKey(e: KeyboardEvent): void {
    const target = e.target as HTMLElement | null;
    // Never steal digits from a HUD text field.
    if (target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) return;
    switch (e.key) {
      case '1':
        this.setView('perspective');
        break;
      case '2':
        this.setView('top');
        break;
      case '3':
        this.setView('presentation');
        break;
      default:
        break;
    }
  }

  /** Switch camera view and keep the toolbar's pressed state in sync. */
  setView(view: CameraViewName): void {
    const modes: Record<CameraViewName, CameraViewMode> = {
      perspective: CameraViewMode.Perspective,
      top: CameraViewMode.Top,
      presentation: CameraViewMode.Presentation,
    };
    this.camera.setViewMode(modes[view]);
    this.hud?.setActiveView(view);
  }

  /* --------------------------------- Reference grid --------------------------------- */

  /** Whether the lettered reference grid is drawn over the site. */
  get gridVisible(): boolean {
    return this.gridOverlay?.visible ?? false;
  }

  /**
   * Show or hide the reference grid. Passing nothing flips it.
   *
   * This is the only switch: the overlay's visibility is also what decides whether the wand is told
   * about the grid at all (see {@link createWandTool}). Keeping one flag for both means the frame the
   * wand reasons in is always the frame the designer is looking at — a grid the model can name but
   * the user cannot see would be worse than no grid.
   */
  setGridVisible(visible?: boolean): void {
    const grid = this.gridOverlay;
    if (!grid) return;
    grid.setVisible(visible ?? !grid.visible);
    this.hud?.requestRefresh();
  }

  /* -------------------------------- Topographic map -------------------------------- */

  /** Whether the site is drawn with contour lines over it. */
  get topoVisible(): boolean {
    return this.contourOverlay?.visible ?? false;
  }

  /** How the contour map is labelling its elevations. */
  get topoLabelMode(): ContourLabelMode {
    return this.contourOverlay?.labelMode ?? 'auto';
  }

  /** Vertical spacing between contours on the current map, in metres. Zero when nothing is traced. */
  get topoIntervalMeters(): number {
    return this.contourOverlay?.contourIntervalMeters ?? 0;
  }

  /** Spacing between the numbered contours, or zero when every line carries its elevation. */
  get topoIndexIntervalMeters(): number {
    return this.contourOverlay?.indexIntervalMeters ?? 0;
  }

  /**
   * Show or hide the topographic overlay. Passing nothing flips it.
   *
   * This is the *deliberate* switch, and using it forfeits the automatic restore: once you have said
   * what you want the map to do while sculpting, leaving the sculpt tool must not overrule you.
   */
  setTopoVisible(visible?: boolean): void {
    const contours = this.contourOverlay;
    if (!contours) return;
    contours.setVisible(visible ?? !contours.visible);
    this.topoBeforeSculpt = null;
    this.hud?.requestRefresh();
  }

  /** Step the elevation figures through auto → always → off. */
  cycleTopoLabels(): ContourLabelMode {
    const mode = this.contourOverlay?.cycleLabelMode() ?? 'auto';
    this.hud?.requestRefresh();
    return mode;
  }

  /**
   * Bring the topo map in and out with the sculpt tool.
   *
   * Sculpting is the one activity that is *about* elevation, so the map arrives with the tool rather
   * than waiting to be asked for. What matters as much is that it leaves again: an overlay that
   * turns itself on and then stays on has not helped you, it has changed your workspace behind your
   * back. So the state it interrupted is remembered and put back — unless you touched the switch
   * yourself in the meantime, in which case your word is the last one.
   */
  private syncTopoToSculpt(sculpting: boolean): void {
    const contours = this.contourOverlay;
    if (!contours) return;
    const next = topoSculptTransition(sculpting, contours.visible, this.topoBeforeSculpt);
    contours.setVisible(next.visible);
    this.topoBeforeSculpt = next.remembered;
  }

  /**
   * Frame the camera on the given selection, or on the whole site when nothing is selected.
   *
   * Plants are measured by their canopy, because that is what "frame this tree" means. A creature or
   * an object is measured by where its *view* ended up rather than by its record, since both store a
   * height offset that the world adds a ground height to — framing on the record would aim the camera
   * at sea level under a hillside.
   */
  focusSelection(ids: readonly string[]): void {
    const world = this.floraEditor?.world;
    if (!world || ids.length === 0) {
      const span = Math.max(this.terrain?.widthMeters ?? 30, this.terrain?.lengthMeters ?? 30);
      this.camera.focusOn(new THREE.Vector3(0, 0, 0), span * 1.1);
      return;
    }
    const center = new THREE.Vector3();
    let radius = 2;
    let counted = 0;
    for (const id of ids) {
      const instance = world.get(id);
      if (instance) {
        center.add(new THREE.Vector3(instance.position.x, instance.position.y, instance.position.z));
        radius = Math.max(radius, world.growthState(instance).spreadMeters);
        counted += 1;
        continue;
      }
      const view = this.fauna.view(id) ?? this.siteObjects.view(id);
      if (!view) continue;
      center.add(view.position);
      counted += 1;
    }
    if (counted === 0) return;
    center.divideScalar(counted);
    this.camera.focusOn(center, radius * 4);
  }

  /**
   * The ground materials this site can be painted with.
   *
   * Exposed for the same reason `terrain` is: the end-to-end harness drives the real application
   * through this object, and reaching the palette by importing its module instead would get a second
   * copy of the singleton under the dev server's module graph — which looks exactly like a broken
   * subscription and is not one.
   */
  get surfacePalette(): SurfacePalette {
    return surfacePalette;
  }

  /**
   * Open the dialog that makes a ground material. Built on first use and kept, so a second material
   * does not pay to construct a dialog again.
   */
  private openTextureStudio(): void {
    const hud = this.hud;
    if (!hud) return;
    this.textureStudio ??= new TextureStudio({
      host: () => hud.root,
      apiKey: () => this.apiKeys.get(),
      onChange: () => {
        // The material exists now; the ground has to grow a splat page for it before anything is
        // painted, and the site is dirty because its palette is part of the document.
        this.terrain?.syncPalette();
        this.projectMenu?.markDirty();
        this.hud?.requestRefresh();
      },
      onPick: (layer) => {
        this.setGroundTool('paint');
        this.terrainEditor?.setPaintLayer(layer.id);
        this.hud?.requestRefresh();
      },
    });
    this.textureStudio.open();
  }

  /**
   * Retire a material this site added.
   *
   * Two halves that are recorded differently, on purpose. Taking the paint out of the ground is an
   * *edit* — it changes the garden, so it goes on the undo stack as an ordinary paint command and
   * Ctrl+Z brings the surface back. Removing the material from the palette is not an edit to the
   * garden but a change to what the garden *can be*, and putting that on the same stack would make
   * one Ctrl+Z restore the paint while leaving the material gone, which is the worst of both.
   *
   * So the palette entry goes immediately and the paint goes as an undoable command. Undoing brings
   * back weights in a channel nothing owns — invisible until the material is made again — which is
   * an honest state, and the panel says as much before anything is removed.
   */
  private retireSurfaceLayer(layer: SurfaceLayer): void {
    const terrain = this.terrain;
    if (terrain) {
      const before = terrain.surface.readRect({
        x: 0,
        y: 0,
        width: terrain.surface.width,
        height: terrain.surface.height,
      });
      const rect = terrain.clearSurfaceChannel(layer.channel);
      if (rect) {
        const command = new PaintStrokeCommand(
          terrain,
          rect,
          before,
          terrain.surface.readRect(rect),
          `Retire ${layer.name.toLowerCase()}`,
        );
        if (!command.isEmpty) this.floraEditor?.history.push(command);
      }
    }
    surfacePalette.remove(layer.id);
    this.terrain?.syncPalette();
    this.projectMenu?.markDirty();
    this.hud?.requestRefresh();
  }

  /* ------------------------------- Staffage tools ------------------------------- */

  /**
   * The fauna tool: pick a creature and a pose in the palette, click the ground to place it.
   *
   * A click places the *group* the record says is normal — one deer, four goldfinches — as a single
   * undo step, with `Alt` for exactly one. That is the whole payoff of `FaunaGrouping`: the catalog
   * already knows a lone goldfinch is unusual, and a tool that ignored it would make the designer
   * click fourteen times to get an ordinary sight.
   */
  private createFaunaTool(): StaffageTool {
    return new StaffageTool({
      domElement: this.renderer.domElement,
      getCamera: () => this.camera.activeCamera,
      terrain: () => this.terrain,
      buildGhost: (id) => this.buildFaunaGhost(id),
      onPlace: (id, point, options) => this.placeFauna(id, point, options.single),
      onChange: () => this.syncStaffageInput(),
      onNotice: (text) => this.hud?.noteAtCursor(text),
    });
  }

  private createObjectTool(): StaffageTool {
    return new StaffageTool({
      domElement: this.renderer.domElement,
      getCamera: () => this.camera.activeCamera,
      terrain: () => this.terrain,
      buildGhost: (id) => this.buildObjectGhost(id),
      // Yaw zero, deliberately, and the opposite of the fauna default: a row of benches facing the
      // same way is right, and a row of identically-oriented rabbits is the tell that a scene was
      // placed by a machine. See `StaffagePlacement.headingFor`.
      onPlace: (id, point) => {
        this.placeProp(id, { x: point.x, z: point.z });
      },
      onChange: () => this.syncStaffageInput(),
      onNotice: (text) => this.hud?.noteAtCursor(text),
    });
  }

  /**
   * Hand the canvas to whichever staffage tool is armed, or back to the flora editor when neither
   * is.
   *
   * Without this the flora editor's own pointer handlers stay live under the tool, so a click meant
   * to drop a rabbit would also clear or change the plant selection behind it — the same collision
   * the tag tool already solves this way.
   */
  private syncStaffageInput(): void {
    const armed = this.faunaTool?.isActive === true || this.objectTool?.isActive === true;
    this.floraEditor?.setInputEnabled(!armed);
    this.hud?.requestRefresh();
  }

  /** Put both staffage tools down. Called when anything else takes the canvas. */
  private cancelStaffageTools(): void {
    this.faunaTool?.cancel();
    this.objectTool?.cancel();
  }

  /** Arm the fauna tool, taking the canvas from whatever else held it. */
  armFauna(definitionId: string, poseId: string): void {
    const definition = this.faunaCatalog.find(definitionId);
    if (!definition || !this.faunaTool) return;
    this.armedFaunaPose = poseId;
    this.objectTool?.cancel();
    this.setGroundTool(null);
    this.setWandTool(false);
    this.setTagTool(false);
    this.floraEditor?.cancelPlacement();
    const group = groupSizeFor(definition, false);
    this.faunaTool.start(
      definitionId,
      group > 1 ? `${definition.name} · ×${group} — hold Alt for one` : definition.name,
    );
    this.hud?.requestRefresh();
  }

  /** Arm the object tool. */
  armObject(propId: string): void {
    const definition = propCatalog.get(propId);
    if (!definition || !this.objectTool) return;
    this.faunaTool?.cancel();
    this.setGroundTool(null);
    this.setWandTool(false);
    this.setTagTool(false);
    this.floraEditor?.cancelPlacement();
    // Started before the asset has necessarily arrived — the ghost is massing until it does, exactly
    // as the placed object would be.
    void propLibrary.ensureLoaded(propId);
    this.objectTool.start(propId, definition.name);
    this.hud?.requestRefresh();
  }

  /** What the fauna tool has armed, for the palette's selected state. */
  armedFauna(): { definitionId: string; poseId: string } | null {
    const id = this.faunaTool?.activeId ?? null;
    if (!id) return null;
    return { definitionId: id, poseId: this.armedFaunaPose ?? '' };
  }

  armedObject(): string | null {
    return this.objectTool?.activeId ?? null;
  }

  /**
   * Place a creature, or its usual group, at a world point — undoably.
   *
   * The group is scattered by `scatterStaffage`, whose first member lands exactly on the click: a
   * designer aiming at a particular flagstone expects something to be on it.
   */
  placeFauna(definitionId: string, point: THREE.Vector3, single = false): void {
    const definition = this.faunaCatalog.find(definitionId);
    if (!definition) return;

    const pose =
      definition.poses.find((candidate) => candidate.id === this.armedFaunaPose) ??
      defaultPose(definition);
    const attachment = poseAttachments(definition, pose)[0] ?? 'ground';
    const seed = Math.floor(Math.random() * 0x7fffffff);

    const members = scatterStaffage({
      count: groupSizeFor(definition, single),
      spreadMeters: definition.grouping.spreadMeters,
      ...(definition.sizeVariation !== undefined
        ? { sizeVariation: definition.sizeVariation }
        : {}),
      seed,
    }).map((offset) =>
      createFaunaInstance(definition, {
        position: { x: point.x + offset.dx, y: 0, z: point.z + offset.dz },
        rotationY: offset.rotationY,
        poseId: pose.id,
        attachment,
        sizeVariation: offset.sizeVariation,
        visualSeed: offset.visualSeed,
      }),
    );

    const editor = this.floraEditor;
    if (editor) {
      editor.history.execute(
        new AddFaunaCommand(
          this.fauna,
          members,
          members.length === 1 ? `Place ${definition.name.toLowerCase()}` : `Place ${members.length} ${definition.name.toLowerCase()}`,
        ),
      );
    } else {
      for (const member of members) this.fauna.add(member);
    }

    this.projectMenu?.markDirty();
    this.hud?.requestRefresh();
  }

  /** A translucent preview of the armed creature, lifted to its hover height when it flies. */
  private buildFaunaGhost(definitionId: string): THREE.Object3D | null {
    const definition = this.faunaCatalog.find(definitionId);
    if (!definition) return null;
    const pose =
      definition.poses.find((candidate) => candidate.id === this.armedFaunaPose) ??
      defaultPose(definition);
    const attachment = poseAttachments(definition, pose)[0] ?? 'ground';
    const instance = createFaunaInstance(definition, {
      poseId: pose.id,
      attachment,
      visualSeed: 1,
      rotationY: headingFor(1),
    });
    const view = this.ghostFactory.build(definition, instance);
    // The tool moves its overlay group to the ground point, so the ghost carries only the lift its
    // attachment implies.
    view.position.set(0, defaultHeightOffset(definition, attachment), 0);
    return view;
  }

  /**
   * A preview of the armed object.
   *
   * Deliberately a plain box at the catalog's measured size rather than the asset: the mesh may not
   * have downloaded, and a ghost that pops from box to bench mid-hover is worse than one that was
   * always a box. It is the correct size either way, which is what the preview is for.
   */
  private buildObjectGhost(propId: string): THREE.Object3D | null {
    const definition = propCatalog.get(propId);
    if (!definition) return null;
    const { widthMeters, heightMeters, depthMeters } = definition.size;
    const mesh = new THREE.Mesh(
      new THREE.BoxGeometry(widthMeters, heightMeters, depthMeters),
      new THREE.MeshStandardMaterial({
        color: new THREE.Color(definition.colors.body),
        flatShading: true,
        roughness: 0.8,
      }),
    );
    mesh.position.y = heightMeters / 2;
    return mesh;
  }

  private createHud(editor: FloraEditor): Hud {
    return new Hud({
      container: this.container,
      editor,
      // Lets the HUD draw the pointer per tool over the canvas.
      ...(this.renderer.domElement ? { canvas: this.renderer.domElement } : {}),
      ...(this.terrainEditor ? { terrainEditor: this.terrainEditor } : {}),
      fauna: {
        catalog: this.faunaCatalog,
        armed: () => this.armedFauna(),
        onPick: (definitionId, poseId) => this.armFauna(definitionId, poseId),
      },
      objects: {
        catalog: propCatalog,
        armed: () => this.armedObject(),
        onPick: (propId) => this.armObject(propId),
      },
      onGroundTool: (kind) => this.setGroundTool(kind),
      groundCoverage: () => this.terrain?.coverage() ?? null,
      onNewMaterial: () => this.openTextureStudio(),
      onRetireMaterial: (layer) => this.retireSurfaceLayer(layer),
      environment: this.environment,
      onSetView: (view) => this.setView(view),
      onFocusSelection: () => this.focusSelection(editor.selection.selected()),
      onWandTool: (active) => this.setWandTool(active),
      wandActive: () => this.wandTool?.isActive ?? false,
      onTagTool: (active) => this.setTagTool(active),
      tagActive: () => this.tagTool?.isActive ?? false,
      semanticsFor: (instanceId) => this.semanticsFor({ kind: 'plant', instanceId }),
      siteSemantics: () => this.semanticsFor({ kind: 'site' }),
      onToggleGrid: () => this.setGridVisible(),
      gridVisible: () => this.gridVisible,
      onToggleTopo: () => this.setTopoVisible(),
      topoVisible: () => this.topoVisible,
      onCycleTopoLabels: () => this.cycleTopoLabels(),
      topoLabelMode: () => this.topoLabelMode,
      topoIntervalMeters: () => this.topoIntervalMeters,
      topoIndexIntervalMeters: () => this.topoIndexIntervalMeters,
      // The project menus are created just after the HUD, so these resolve at click time.
      onNewSite: () => this.projectMenu?.openNewSite(),
      onOpen: () => this.projectMenu?.openLibrary(),
      onSave: () => this.projectMenu?.save(),
      onSaveAs: () => this.projectMenu?.openSaveAs(),
      onRename: () => this.projectMenu?.openRename(),
      onTogglePhotoMode: () => this.togglePhotoMode(),
      onOpenShots: () => this.openShots(),
      site: () =>
        this.terrain
          ? {
              widthMeters: this.terrain.widthMeters,
              lengthMeters: this.terrain.lengthMeters,
              templateName: getTerrainTemplate(this.terrain.params.templateId).name,
            }
          : null,
      fps: () => this.smoothedFps,
      localization: {
        localization: () => this.localization,
        onLocalize: (profile, options) => this.localizeSite(profile, options ?? {}),
        onConditions: (conditions) => this.setSiteConditions(conditions),
      },
      // Read per call, both of them: the badge follows the session and the key row follows the
      // store, so signing in or pasting a key redraws the panel without it holding either.
      license: {
        apiKeys: this.apiKeys,
        license: () => licenseFor(this.session.current),
        onManageAccount: () => this.accountMenu?.open(),
      },
      floraResolver: this.flora,
      appVersion: APP_VERSION,
      // Read per call: signing in moves the library out from under the app, and the info sheet is
      // exactly where someone checks which of the two shapes they are working in.
      storageLabel: () =>
        this.session.current.user === null ? 'this browser' : 'your account',
      // Always available from the info sheet, whether or not it was ever raised automatically:
      // the reader who skipped it on day one is exactly the one who comes looking for it later.
      onTour: () => this.tour.start(),
      ...(this.readout ? { readout: this.readout } : {}),
    });
  }

  /* ----------------------------------- The wand ----------------------------------- */

  private createWandTool(editor: FloraEditor): WandTool {
    return new WandTool({
      domElement: this.renderer.domElement,
      getCamera: () => this.camera.activeCamera,
      overlayRoot: this.scene.overlayRoot,
      terrain: () => this.terrain,
      world: () => editor.world,
      catalog: editor.catalog,
      siteObjects: () => this.siteObjects,
      environment: this.environment,
      selectedIds: () => editor.selection.selected(),
      // Only while the overlay is on — the wand is given the cell names the designer can read, and
      // nothing when there are none on screen.
      grid: () => (this.gridOverlay?.visible ? this.gridOverlay.gridSpec : null),
      floraRoot: this.scene.floraRoot,
      siteRoot: this.scene.siteRoot,
      onTarget: (context, screen) => this.wandBubble?.open(context, screen),
    });
  }

  private createWandBubble(hud: Hud, editor: FloraEditor): WandBubble {
    return new WandBubble({
      host: hud.root,
      catalog: editor.catalog,
      keys: this.apiKeys,
      apply: (spell, attachments) => this.applyWandSpell(spell, editor, attachments),
      undo: () => editor.undo(),
      onClose: () => this.wandTool?.clearAnchor(),
      onBusyChange: (busy) => {
        this.wandBusy = busy;
      },
      displayUnit: () => hud.preferences.unit,
    });
  }

  /**
   * Switch the wand on or off.
   *
   * Exclusivity is arbitrated here for the same reason the ground tools' is: neither editor should
   * have to know the other exists. The wand takes the canvas from whoever holds it, and hands it back
   * to the flora editor on the way out.
   */
  setWandTool(active: boolean): void {
    const wand = this.wandTool;
    if (!wand) return;
    if (active) {
      this.setGroundTool(null);
      this.setTagTool(false);
      this.cancelStaffageTools();
      this.floraEditor?.setInputEnabled(false);
      wand.activate();
    } else {
      wand.deactivate();
      this.wandBubble?.close();
      this.floraEditor?.setInputEnabled(true);
    }
    this.hud?.requestRefresh();
  }

  /**
   * Apply a spell to the live site.
   *
   * Goes onto the flora editor's history, which is the same timeline the ground tools share — so one
   * Ctrl+Z takes back a wand result whether it sculpted, painted, planted, built, or all four.
   */
  private applyWandSpell(
    spell: WandSpell,
    editor: FloraEditor,
    attachments: readonly TagImage[] = [],
  ): WandReport {
    const terrain = this.terrain;
    if (!terrain) {
      return { ...emptyWandReport(), warnings: [{ message: 'There is no terrain to edit.' }] };
    }

    const report = applyWandSpell(
      spell,
      {
        terrain,
        world: editor.world,
        catalog: editor.catalog,
        siteObjects: this.siteObjects,
        environment: this.environment,
        tags: this.tags,
        // The turn's photographs, offered to the spell. Only the ones a `tag` op points at are kept
        // — see `WandOps.TagOp`.
        attachments,
        selectedIds: () => editor.selection.selected(),
        // Varies the planting layout between casts without varying anything the model said.
        seed: Math.max(1, Math.trunc(this.elapsed * 1000) % 0x7fffffff),
        onGroundChanged: (rect) => this.handleGroundChanged(rect),
      },
      editor.history,
    );

    // Fresh plants arrive without a season applied, and a build or a sculpt moves what sits on the
    // ground; re-running both is cheaper than teaching every op to do its own.
    editor.world.applySeason(this.environment.effectiveDayOfYear());
    editor.refresh();
    this.applyLens();
    this.hud?.requestRefresh();
    return report;
  }

  /* --------------------------------- Semantic tags --------------------------------- */

  private createTagTool(editor: FloraEditor): TagTool {
    return new TagTool({
      domElement: this.renderer.domElement,
      getCamera: () => this.camera.activeCamera,
      terrain: () => this.terrain,
      tags: this.tags,
      floraRoot: this.scene.floraRoot,
      siteRoot: this.scene.siteRoot,
      resolveAnchor: (target) => this.tagAnchor(target, editor),
      onTarget: (pick, screen) => this.tagBubble?.open(pick, screen),
    });
  }

  private createTagBubble(hud: Hud, editor: FloraEditor): TagBubble {
    return new TagBubble({
      host: hud.root,
      tagsFor: (target) => this.tags.forTarget(target),
      profileFor: (target) => this.semanticsFor(target, editor),
      describeTarget: (target) => this.describeTagTarget(target, editor),
      groundLayerAt: (x, z) => {
        const layer = this.terrain?.sampleSurface(x, z);
        return layer === undefined ? null : { kind: 'ground', layer };
      },
      // Every write goes through the shared history, so Ctrl+Z walks back through tags and planting
      // in the order they happened rather than through two timelines that interleave.
      addTag: (tag) => this.commitTag(new AddTagCommand(this.tags, tag, `Tag “${tag.label}”`), editor),
      updateTag: (id, patch, label, mergeKey) => {
        const before = this.tags.get(id);
        if (!before) return;
        const after: SemanticTag = { ...cloneTag(before), ...patch, id: before.id };
        this.commitTag(new UpdateTagCommand(this.tags, before, after, label, mergeKey), editor);
      },
      removeTag: (id) => {
        const tag = this.tags.get(id);
        if (!tag) return;
        this.commitTag(new RemoveTagsCommand(this.tags, [tag]), editor);
      },
    });
  }

  private commitTag(command: Parameters<FloraEditor['history']['execute']>[0], editor: FloraEditor): void {
    editor.history.execute(command);
    this.projectMenu?.markDirty();
    this.hud?.requestRefresh();
  }

  /**
   * Where a tag's marker belongs in world space.
   *
   * Ground-material and whole-site tags return null deliberately: they are true of the site rather
   * than of a point on it, and dropping a pin somewhere arbitrary to represent one would be a claim
   * about location that the tag does not make.
   */
  private tagAnchor(
    target: TagTarget,
    editor: FloraEditor,
  ): { x: number; y: number; z: number } | null {
    const ground = (x: number, z: number): number => this.terrain?.sampleHeight(x, z) ?? 0;
    switch (target.kind) {
      case 'location':
        return { x: target.x, y: ground(target.x, target.z), z: target.z };
      case 'plant': {
        const instance = editor.world.get(target.instanceId);
        if (!instance) return null;
        // Above the canopy rather than at the base: a pin inside a shrub is a pin nobody can read.
        const height = editor.world.growthState(instance).heightMeters;
        return { x: instance.position.x, y: instance.position.y + height, z: instance.position.z };
      }
      case 'structure': {
        const object = this.siteObjects.get(target.objectId);
        if (!object) return null;
        const top = object.kind === 'paving' ? 0 : Math.max(object.heightMeters, 0);
        return {
          x: object.position.x,
          y: ground(object.position.x, object.position.z) + object.position.y + top,
          z: object.position.z,
        };
      }
      default:
        return null;
    }
  }

  /**
   * Everything known about a tag target — the implicit half from the catalogs and the record, plus
   * whatever has been tagged on it by hand.
   *
   * This is the single assembly point the inspector and the capture manifest both read through, so
   * "what will the render be told about this?" has one answer rather than two that drift.
   */
  semanticsFor(target: TagTarget, editor?: FloraEditor): SemanticProfile | null {
    const flora = editor ?? this.floraEditor;
    const manual = this.tags.forTarget(target);

    switch (target.kind) {
      case 'plant': {
        const instance = flora?.world.get(target.instanceId);
        if (!instance || !flora) return null;
        return floraSemantics(flora.catalog.get(instance.floraDefinitionId), instance, manual);
      }
      case 'structure': {
        const object = this.siteObjects.get(target.objectId);
        if (!object) return null;
        return structureSemantics(object, propLibrary.definitionFor(object), manual);
      }
      case 'ground':
        return groundSemantics(target.layer, manual);
      case 'location':
      case 'site':
        // Neither has an implicit half — a place and a garden mean exactly what they have been told
        // to mean — so the profile is the manual tags and nothing else.
        return {
          subject: target.kind === 'site' ? 'This garden' : 'This place',
          descriptors: manual.map((tag) => ({
            category: tag.category,
            label: tag.label,
            ...(tag.note !== undefined ? { note: tag.note } : {}),
            emphasis: tag.emphasis,
            source: 'manual' as const,
            tagId: tag.id,
          })),
        };
    }
  }

  /**
   * A tag target's name, for the bubble's header, its scope chips, and the tag index's rows.
   *
   * The editor is nullable because the index outlives any one of its callers' guarantees: a tag list
   * asked for before the flora editor exists should say "Plant" rather than throw, on the same
   * principle the tag parsers follow — a name that has degraded is still a usable list.
   */
  private describeTagTarget(target: TagTarget, editor: FloraEditor | null): string {
    switch (target.kind) {
      case 'site':
        return 'Whole site';
      case 'ground':
        return findSurfaceLayer(target.layer)?.name ?? target.layer;
      case 'location':
        return 'This spot';
      case 'plant': {
        const instance = editor?.world.get(target.instanceId);
        if (!instance) return 'Plant';
        const definition = editor?.catalog.find(instance.floraDefinitionId);
        return definition?.commonNames[0] ?? definition?.botanicalName ?? 'Plant';
      }
      case 'structure':
        return this.siteObjects.get(target.objectId)?.name ?? 'Structure';
    }
  }

  /**
   * Select and frame whatever a tag is about — the tag index's way back into the model.
   *
   * Returns false for ground-material and whole-site tags rather than inventing somewhere to point
   * the camera. Those tags are true of the site rather than of a place in it, and flying to an
   * arbitrary gravel texel would be a claim about location the tag does not make — the same
   * distinction {@link tagAnchor} already draws for the markers.
   */
  revealTag(tag: SemanticTag): boolean {
    const editor = this.floraEditor;
    const anchor = editor ? this.tagAnchor(tag.target, editor) : null;
    if (!anchor) return false;

    const id = taggedEntityId(tag);
    if (id !== undefined && editor) {
      // Selecting as well as framing, because the question behind "show me" is usually the next
      // edit: the thing is lit up and ready for the inspector by the time the camera settles.
      editor.selection.selectOnly([id]);
      this.focusSelection([id]);
      return true;
    }

    // A location tag has no record to select, so the reach it claims becomes the framing distance —
    // a three-metre seating area and a thirty-metre wild corner should not arrive at the same zoom.
    const radius = tag.target.kind === 'location' ? tag.target.radiusMeters : 4;
    this.camera.focusOn(new THREE.Vector3(anchor.x, anchor.y, anchor.z), Math.max(6, radius * 3));
    return true;
  }

  /**
   * Switch the tag tool on or off.
   *
   * Exclusivity is arbitrated here for the same reason the wand's and the ground tools' is: no editor
   * should have to know the others exist. The tag tool takes the canvas from whoever holds it and
   * hands it back to the flora editor on the way out.
   */
  setTagTool(active: boolean): void {
    const tool = this.tagTool;
    if (!tool) return;
    if (active) {
      this.setGroundTool(null);
      this.setWandTool(false);
      this.cancelStaffageTools();
      this.floraEditor?.setInputEnabled(false);
      tool.activate();
    } else {
      tool.deactivate();
      this.tagBubble?.close();
      this.floraEditor?.setInputEnabled(true);
    }
    this.hud?.requestRefresh();
  }

  /* ------------------------------------ Props ------------------------------------ */

  /**
   * Place a catalog prop on the site, undoably.
   *
   * The record is created and added synchronously — sized and coloured from the catalog, so it lands
   * as correct massing — while its mesh downloads in the background and replaces the massing when it
   * arrives. Returns the object so a caller can select or move it straight away.
   *
   * Goes onto the flora editor's history, the same timeline the ground tools and the wand share, so
   * one Ctrl+Z takes back a placed bench exactly as it takes back a planted tree.
   */
  placeProp(propId: string, position: { x: number; z: number }, rotationY = 0): SiteObject | null {
    const editor = this.floraEditor;
    const y = this.terrain?.sampleHeight(position.x, position.z) ?? 0;
    const object = propLibrary.createProp(propId, {
      position: { x: position.x, y: 0, z: position.z },
      rotationY,
    });
    // `y` is an offset above the sampled ground, which the world re-derives per footprint; the sample
    // here only tells us the prop is over real terrain rather than off the edge of the site.
    void y;

    if (editor) {
      editor.history.execute(new AddSiteObjectsCommand(this.siteObjects, [object], 'Place prop'));
    } else {
      this.siteObjects.add(object);
    }

    this.projectMenu?.markDirty();
    this.hud?.requestRefresh();
    return object;
  }

  /** The prop catalog, for a picker UI and for tooling that wants to enumerate what can be placed. */
  get props(): PropDefinition[] {
    return propLibrary.definitions;
  }

  /* --------------------------------- Ground tools --------------------------------- */

  /**
   * Switch the ground tool on or off. Passing `null` hands the canvas back to the flora editor.
   *
   * Exclusivity is arbitrated here rather than inside either editor, because neither of them should
   * have to know the other exists — the flora editor simply stops listening, and the ground tool
   * simply starts.
   */
  setGroundTool(kind: TerrainToolKind | null): void {
    const terrainEditor = this.terrainEditor;
    if (!terrainEditor) return;
    if (kind === null) {
      terrainEditor.deactivate();
      this.floraEditor?.setInputEnabled(true);
    } else {
      // The wand and the tag tool hold the canvas the same way a ground tool does, and `B`/`T` reach
      // here straight from the keyboard without passing the toolbar's own exclusivity check — so it
      // is enforced here too, or their click handlers would stay live underneath the brush.
      this.setWandTool(false);
      this.setTagTool(false);
      this.cancelStaffageTools();
      this.floraEditor?.setInputEnabled(false);
      terrainEditor.activate(kind);
    }
    // After the tool has actually changed, not before: the overlay follows what is in hand, and the
    // calls above are the only thing that decides that.
    this.syncTopoToSculpt(terrainEditor.activeTool === 'sculpt');
    this.hud?.requestRefresh();
  }

  /** Keyboard path: `B` and `T` toggle their tool rather than only turning it on. */
  toggleGroundTool(kind: TerrainToolKind): void {
    this.setGroundTool(this.terrainEditor?.activeTool === kind ? null : kind);
  }

  /**
   * Re-derive everything that sits on the ground after the sculpt tool moved it.
   *
   * Plants and buildings store no height of their own that the terrain does not imply, so this is a
   * recomputation and not an edit — which is why a sculpt undo needs to record only the heights: it
   * restores them and calls back here, and the plants follow.
   *
   * Work is limited to the stroke's own footprint, grown by a margin so a plant just outside the
   * edited vertices still settles onto the new slope its neighbouring vertex now describes.
   */
  private handleGroundChanged(rect: SurfaceRect): void {
    const terrain = this.terrain;
    if (!terrain) return;
    const min = vertexToWorld(terrain.field, rect.x, rect.y);
    const max = vertexToWorld(terrain.field, rect.x + rect.width - 1, rect.y + rect.height - 1);
    const marginX = terrain.widthMeters / Math.max(1, terrain.field.cols - 1);
    const marginZ = terrain.lengthMeters / Math.max(1, terrain.field.rows - 1);

    this.floraEditor?.regroundPlants(
      (x, z) =>
        x >= min.x - marginX &&
        x <= max.x + marginX &&
        z >= min.z - marginZ &&
        z <= max.z + marginZ,
    );
    // Buildings sample the ground for their floor level; re-running the sampler re-seats them.
    this.siteObjects.setGroundSampler((x, z) => terrain.sampleHeight(x, z));
    this.fauna.setGroundSampler((x, z) => terrain.sampleHeight(x, z));
    // The grid is draped on the ground too, and a grid buried in a fresh berm names the wrong cell
    // to the eye. Re-draping rewrites the existing buffer, so this is cheap enough per stroke.
    if (this.gridOverlay?.visible) this.gridOverlay.refreshHeights();
    // The contour map is a *drawing of* the heights, so it cannot be patched — it is re-traced. That
    // is too much to do per frame, so this only marks it stale and the overlay picks its own moment.
    this.contourOverlay?.markDirty();
  }

  /* ----------------------------------- Hosted tier ---------------------------------- */

  /**
   * Ask the server who we are, and switch the app's storage to match.
   *
   * Runs once, after the first frame is on its way. Three outcomes, and all three are normal:
   *
   *   **No server.** The probe fails or 404s, `authAvailable` stays false, and nothing changes —
   *   the app is the single-user tool, sites in IndexedDB, AI on a browser-held key.
   *
   *   **Server, not signed in.** A sign-in control appears in the toolbar. Storage stays local, so
   *   an anonymous visitor can use every feature of the editor before deciding to make an account.
   *
   *   **Signed in.** Projects and photos move to the account, and AI moves behind the proxy so the
   *   browser never holds a key.
   *
   * The stores are swapped by rebuilding the two menus that own them. That is heavier than mutating
   * a field, and it is the reason it is done this way: `ProjectMenu` holds an autosave service, an
   * unload guard, and a document identity that all belong to *a* store, and quietly repointing them
   * mid-session is how a save ends up written to the place the user just left.
   */
  private async connectSession(): Promise<void> {
    const state = await this.session.refresh();
    if (!this.running) return;

    this.applySession(state);
    void this.offerFirstRunSite();
    void this.offerTour(state);
    // Later transitions — signing in, signing out, or a session expiring under a running app — take
    // the same path, so there is exactly one description of what each mode means.
    this.unsubscribe.push(
      this.session.onChange((next) => {
        if (!this.running) return;
        this.applySession(next);
        void this.offerFirstRunSite();
        // Signing in makes a first-time visitor of someone who has already used this browser: the
        // tour is remembered per account, so a designer opening their account on a second machine
        // still gets the opening, and a shared workstation does not spend one person's tour on
        // another person's session. See `Tour.audienceFor`.
        void this.offerTour(next);
      }),
    );
  }

  /**
   * Open the site this session should land on, rather than the blank canvas.
   *
   * A returning user is resumed onto the site they last had open; someone who has never been here
   * gets a finished site from the starter library, which one being the server's decision so the
   * site that greets a new user can be changed without shipping a client. `ProjectMenu` picks
   * between the two, because it is what holds the store that answers the first question.
   *
   * Runs in every mode, not only against an account. The blank canvas was as wrong for the
   * standalone tool as for the hosted one — a site is worked on over weeks, and the one you were
   * in yesterday is the one you want this morning. Only the starter half needs a server, so on a
   * build with none the fallback is simply null.
   *
   * Everything about it is a courtesy: no account, no server, a lapsed session or a library with
   * nothing to resume all resolve to null, and null is the behaviour this app had before the
   * feature existed.
   *
   * Deliberately *not* held behind the opening curtain, unlike the tour. The tour is an overlay
   * that has to be seen from its first frame; this is a document load, and running it under the
   * splash is what makes the editor appear already holding a site instead of visibly swapping one
   * in a moment after the film clears.
   */
  private async offerFirstRunSite(): Promise<void> {
    const menu = this.projectMenu;
    if (!menu) return;
    const starter = this.cloudProjects ? await this.cloudProjects.firstRunSite() : null;
    if (!this.running) return;
    await menu.openOpeningSite(starter);
  }

  /**
   * Raise the tutorial, if this account has never been shown it.
   *
   * Two gates in front of it, and both matter. The curtain, because a tour that plays under the
   * opening film is a tour nobody sees the start of; and the once-per-audience set, because
   * `session.onChange` fires for usage refreshes as well as for sign-ins, and an overlay that
   * reappears every time a render decrements an allowance would be a bug with a very short fuse.
   * Whether the account has *ever* seen it is a separate, persisted question, asked by
   * `TourController.offer`.
   */
  private async offerTour(state: SessionState): Promise<void> {
    const audience = audienceFor(state.user?.id ?? null);
    if (this.tourOffered.has(audience)) return;
    this.tourOffered.add(audience);
    if (this.openingCurtain) await this.openingCurtain;
    if (!this.running) return;
    this.tour.offer(audience);
  }

  private applySession(state: SessionState): void {
    const signedIn = state.user !== null;
    const wasSignedIn = this.cloudProjects !== null;
    if (signedIn === wasSignedIn) {
      // Same mode; only the usage figures moved. Refresh the credential label and stop.
      if (signedIn) this.apiKeys.setManaged(true, describeCredential(state.usage));
      return;
    }

    if (signedIn) {
      this.cloudProjects = new CloudProjectStore({ session: this.session });
      this.cloudPhotos = new CloudPhotoStore({ session: this.session });
      // From here the browser holds no OpenAI key: the server attaches one per request.
      useProxiedAi(true);
      this.apiKeys.setManaged(true, describeCredential(state.usage));
    } else {
      this.cloudProjects?.close();
      this.cloudPhotos?.close();
      this.cloudProjects = null;
      this.cloudPhotos = null;
      useProxiedAi(false);
      this.apiKeys.setManaged(false);
    }

    this.rebuildStorageBoundUi();
  }

  /**
   * Rebuild the two components that hold a store, preserving the open document.
   *
   * The site the user is looking at is captured and re-adopted, so switching storage never costs
   * them their work in progress — it simply starts being saved somewhere else.
   */
  private rebuildStorageBoundUi(): void {
    const hud = this.hud;
    if (!hud) return;

    const previous = this.projectMenu;
    const openDocument = previous ? this.toProject(newProjectIdentity(previous.documentName)) : null;

    previous?.dispose();
    this.projectMenu = this.createProjectMenu(hud);
    if (openDocument !== null) this.projectMenu.adoptRunningDocument(openDocument);

    // Photo mode is only rebuilt if it exists; it is created lazily on first use, and the next
    // creation will pick up the new store on its own.
    if (this.photoMode !== null) {
      const wasActive = this.photoMode.isActive;
      this.photoMode.dispose();
      this.photoMode = this.createPhotoMode();
      if (wasActive) this.photoMode.enter();
    }

    // The browser is not rebuilt — it resolves the store per call, so it is already reading the new
    // one. It does need telling that the shelf changed underneath it while it was open.
    void this.shotsPanel?.refresh();

  }

  /**
   * Put the account control in the toolbar, once, for as long as the HUD lives.
   *
   * Not in {@link rebuildStorageBoundUi} with the two menus that hold a store, which is where this
   * used to be built. That method only runs when the signed-in state *changes*, and an anonymous
   * visitor arriving at a hosted deployment is not a change: `signedIn` and `wasSignedIn` are both
   * false at boot, `applySession` returns early, and the button that is the only way to sign in was
   * never built. The one way to reach it was to be signed in already.
   *
   * It belongs here anyway. Every store this menu touches it reaches through a thunk, so it reads
   * whichever store is current without being rebuilt to be told, and it already redraws itself from
   * `session.onChange` — including hiding entirely when a deployment has no auth configured.
   */
  private mountAccountMenu(hud: Hud): void {
    this.accountMenu?.dispose();
    this.accountMenu = new AccountMenu({
      host: hud.root,
      session: this.session,
      apiKeys: this.apiKeys,
      cloudProjects: () => this.cloudProjects,
      notify: (message) => this.projectMenu?.notify(message),
      onLibraryChanged: () => {
        // Nothing to redraw eagerly: the library screen reads the store when it opens.
      },
    });
    hud.mountAccountControl(this.accountMenu.root);
  }

  private createProjectMenu(hud: Hud): ProjectMenu {
    const menu = new ProjectMenu({
      host: hud.root,
      catalog: () => this.floraEditor?.catalog ?? loadDefaultCatalog(),
      captureProject: (identity) => this.toProject(identity),
      applyProject: (project) => this.applyProject(project),
      newSite: (options) => this.newSite(options),
      plantCount: () => this.floraEditor?.world.count ?? 0,
      onAutosaveStatus: (status) => hud.setSaveStatus(status),
      onDocumentName: (name) => hud.setDocumentName(name),
      signedInAs: () => this.session.current.user?.email ?? null,
      // Only when sites actually go to the account: the saved-site ceiling is a property of a
      // plan, and a library kept in this browser is not counted against one.
      plan: () => (this.cloudProjects === null ? null : licenseFor(this.session.current).plan),
      ...(this.cloudProjects ? { store: this.cloudProjects } : {}),
    });
    hud.setDocumentName(menu.documentName);
    return menu;
  }

  /* ---------------------------------- Localization ---------------------------------- */

  /** Where the site is, or null when it has never been localized. */
  get localization(): SiteLocalization | null {
    return this.siteLocalization ?? null;
  }

  /**
   * Say where this site is.
   *
   * The whole of "applying" a localization is here, and it is deliberately this short: store the
   * profile, drop every derived value, redraw. No flora record is touched, no plant is rewritten, no
   * localized copy of anything is created — every suitability rating, bloom date and berry window on
   * screen a moment later was recomputed from the canonical catalog against the new parameters.
   *
   * Latitude goes to the environment because the sun genuinely moves: a site localized to Vermont
   * should have Vermont's June daylight, and it would be strange to place the garden and leave the
   * sky where it was. That is the one piece of state localization *writes* rather than derives, and
   * it is written once, here.
   *
   * Passing null un-localizes the site, which is a different state from never having localized only
   * in that the user chose it — both draw no badges.
   *
   * `auto` marks a profile the app detected from the browser at startup rather than one the user
   * asked for, and the difference is that it does **not** mark the document dirty. Opening the app,
   * having it quietly work out where you are, and closing the tab must not leave a saved "Untitled
   * site" behind — which is the same rule `isEmptyProject` states about the season and the terrain
   * template, and for the same reason: a thing the app chose for you is not evidence you made
   * something worth keeping. A ZIP typed by hand is a decision, and does mark it.
   */
  localizeSite(localization: SiteLocalization | null, options: { auto?: boolean } = {}): void {
    this.siteLocalization = localization ?? undefined;
    this.flora.invalidate();
    if (localization) this.environment.setLatitude(localization.latitude);
    // Re-colours every plant from the new calendar immediately rather than at the next date change.
    this.floraEditor?.world.applySeason(this.environment.effectiveDayOfYear());
    // And re-reads every lens: localizing moves the windows every resource hangs off, so a bird-food
    // map drawn for Georgia is simply a different map in Vermont.
    this.applyLens();
    this.hud?.requestRefresh();
    if (options.auto !== true) this.projectMenu?.markDirty();
  }

  /**
   * Adjust the site's own conditions — irrigation, shelter — without re-resolving the ZIP.
   *
   * Separate from {@link localizeSite} because these are decisions rather than lookups: a walled
   * garden is a fact about the design, it survives changing the postcode, and it belongs to the same
   * tier of the resolution chain as a plant's own overrides rather than to the climate data.
   */
  setSiteConditions(conditions: SiteConditionsPatch): void {
    if (!this.siteLocalization) return;
    // An explicit `undefined` in the patch erases the decision rather than recording a neutral one,
    // so the cleared keys are dropped rather than carried as present-but-undefined.
    const merged: SiteConditions = { ...this.siteLocalization.conditions };
    for (const [key, value] of Object.entries(conditions)) {
      if (value === undefined) delete merged[key as keyof SiteConditions];
      else Object.assign(merged, { [key]: value });
    }
    const hasAny = Object.keys(merged).length > 0;
    this.siteLocalization = {
      ...this.siteLocalization,
      ...(hasAny ? { conditions: merged } : {}),
    };
    if (!hasAny) delete this.siteLocalization.conditions;
    this.flora.invalidate();
    this.floraEditor?.world.applySeason(this.environment.effectiveDayOfYear());
    this.applyLens();
    this.hud?.requestRefresh();
    this.projectMenu?.markDirty();
  }

  /* -------------------------------- Project document -------------------------------- */

  /**
   * Snapshot the running site as a canonical project document. Nothing renderer-owned is captured —
   * terrain goes out as its generation parameters, plants as their instances — so a reload rebuilds
   * the scene rather than restoring one.
   */
  toProject(identity: ProjectIdentity = newProjectIdentity()): LandscapeProject {
    const terrain: TerrainProjectState = this.terrain?.toProjectState() ?? {
      generation: createGenerationParams('rolling-landscape'),
      units: 'imperial',
    };
    const instances = this.floraEditor?.world.all() ?? [];
    const livePlantIds = new Set(instances.map((instance) => instance.id));
    const liveObjectIds = new Set(this.siteObjects.all().map((object) => object.id));

    // Serializing here (rather than at the storage boundary) means the returned document never
    // aliases live editor state — a saved record cannot change under the store.
    return serializeProject({
      schemaVersion: PROJECT_SCHEMA_VERSION,
      id: identity.id,
      name: identity.name,
      createdAt: identity.createdAt,
      updatedAt: new Date().toISOString(),
      units: terrain.units,
      terrain,
      // Only the added materials; the five that ship in code are not written into every file.
      ...(surfacePalette.custom().length > 0 ? { surfacePalette: surfacePalette.toState() } : {}),
      environment: this.environment.state,
      floraInstances: instances,
      siteObjects: this.siteObjects.all(),
      faunaInstances: this.fauna.all(),
      groups: this.floraEditor?.groups.all() ?? [],
      aliasGroups: deriveAliasGroups(instances),
      // Filtered, not pruned: a tag whose plant was deleted is dropped from the *file*, while the
      // live store keeps it so undoing the delete brings the plant back still described. Mutating the
      // store here would make saving a destructive act.
      semanticTags: this.tags
        .all()
        .filter((tag) => tagTargetExists(tag, livePlantIds, liveObjectIds)),
      // The profile only. Nothing resolved from it is written to the file — see `LandscapeProject`.
      ...(this.siteLocalization ? { localization: this.siteLocalization } : {}),
      cameraViews: [],
      floraCatalogVersion: this.floraEditor?.catalog.version ?? 'unknown',
    });
  }

  /**
   * Rebuild the running site from a project document. Plants whose species is missing from this
   * build's catalog are reported rather than dropped silently — the file still holds them.
   */
  applyProject(project: LandscapeProject): ProjectLoadReport {
    this.setGroundTool(null);
    // A wand request is anchored to a point on the site being replaced, so it cannot survive the swap.
    this.setWandTool(false);
    this.setTagTool(false);
    this.tags.load(project.semanticTags);
    // Before the terrain, and not after: the paint map is sized from the palette's page count, so a
    // site whose ground uses eight materials has to know that before its splat is allocated.
    surfacePalette.load(project.surfacePalette);
    this.loadTerrain(project.terrain.generation, project.terrain.units, project.terrain);
    // Before the environment loads, and before any plant is built: the resolver is what decides
    // every plant's calendar, so adopting the site's climate first means the planting is drawn
    // against it on the first frame rather than being redrawn a moment later.
    this.siteLocalization = project.localization;
    this.flora.invalidate();
    this.environment.load(deserializeEnvironmentState(project.environment));
    // Buildings load before planting so the placement keep-outs are live immediately.
    this.siteObjects.load(project.siteObjects);
    // Prop assets are fetched in the background: the objects are already on screen as correctly-sized
    // massing, and swap to their authored meshes as each file lands. Deliberately not awaited, so
    // opening a project never blocks on the network.
    void propLibrary.preload(project.siteObjects);

    // Creatures naming a record this build does not carry are counted rather than dropped silently,
    // the same tolerance the loader already gives a plant whose species is gone.
    const fauna = this.fauna.load(project.faunaInstances);

    const editor = this.floraEditor;
    if (!editor) {
      return {
        plantCount: 0,
        skippedPlantCount: project.floraInstances.length,
        siteObjectCount: project.siteObjects.length,
        faunaCount: fauna.placed,
        skippedFaunaCount: fauna.skipped,
        tagCount: this.tags.count,
      };
    }

    editor.clearSite();
    const accepted = project.floraInstances.filter((instance) =>
      editor.catalog.has(instance.floraDefinitionId),
    );
    editor.loadInstances(accepted, project.groups);
    editor.world.applySeason(this.environment.effectiveDayOfYear());
    this.applyLens();
    this.hud?.requestRefresh();

    return {
      plantCount: accepted.length,
      skippedPlantCount: project.floraInstances.length - accepted.length,
      siteObjectCount: project.siteObjects.length,
      faunaCount: fauna.placed,
      skippedFaunaCount: fauna.skipped,
      tagCount: this.tags.count,
    };
  }

  /**
   * Replace the site with a fresh one.
   *
   * A blank or terrain-only template gets new ground and nothing else. A **furnished** template —
   * Flat Residential Lot today — builds a whole site instead: it authors the same artifacts the
   * reference pipeline produces and compiles them, so the template arrives with its house, drive,
   * beds and planting rather than as an empty rectangle with a suggestive name.
   */
  newSite(options: NewSiteOptions): ProjectLoadReport | null {
    this.setGroundTool(null);
    this.setWandTool(false);
    this.setTagTool(false);

    if (generatesContent(options)) {
      const project = this.generateSite(options);
      if (project) {
        const report = this.applyProject(project);
        this.hud?.requestRefresh();
        return report;
      }
    }

    // Materials are part of the site they were forged for, so a new site starts on the five that
    // ship in code rather than inheriting whatever the last one collected.
    surfacePalette.reset();
    this.loadTerrain(newSiteParams(options));
    this.floraEditor?.clearSite();
    this.siteObjects.clear();
    this.fauna.clear();
    this.tags.clear();
    // A new site is somewhere else until told otherwise. Carrying the last site's climate over would
    // silently judge a Georgia planting against a New Jersey winter.
    this.siteLocalization = undefined;
    this.flora.invalidate();
    this.environment.load(defaultEnvironmentState());
    this.hud?.requestRefresh();
    return null;
  }

  /**
   * Compile a furnished template into a project.
   *
   * Returns null rather than throwing when the catalog is unavailable, so the caller falls back to
   * plain terrain — a New Site button that fails outright is worse than one that gives you ground.
   */
  private generateSite(options: NewSiteOptions): LandscapeProject | null {
    const catalog = this.floraEditor?.catalog;
    if (!catalog) return null;

    const params = newSiteParams(options);
    const seed = Math.max(1, Math.trunc(options.seed ?? params.seed));
    try {
      const { project } = compilePipeline(
        residentialLotArtifacts({
          widthMeters: params.widthMeters,
          lengthMeters: params.lengthMeters,
          seed,
        }),
        { catalog, seed, name: getTerrainTemplate('flat-residential').name },
      );
      // The generated template keeps its own template id, so the Open dialog and the site summary
      // report what it was made from rather than the compiler's blank canvas.
      project.terrain.generation = { ...project.terrain.generation, templateId: 'flat-residential' };
      return project;
    } catch (error) {
      console.error('Generating the residential lot failed; falling back to plain terrain.', error);
      return null;
    }
  }

  private createPhotoMode(): PhotoMode {
    return new PhotoMode({
      container: this.container,
      capture: this.capture,
      setEditorChromeVisible: (visible) => this.setEditorChromeVisible(visible),
      onRecall: (shot) => this.recallShot(shot),
      onExit: () => this.togglePhotoMode(false),
      apiKeys: this.apiKeys,
      tags: () => this.tags.all(),
      // The plan's export ceiling, or null when nobody is signed in — an anonymous user has no
      // plan, and the standalone tool is not what the subscription gates. See `ExportQuality`.
      exportQuality: () => {
        const plan = licenseFor(this.session.current).plan;
        return plan === null ? null : PLANS[plan].exportQuality;
      },
      ...(this.cloudPhotos ? { store: this.cloudPhotos } : {}),
    });
  }

  /**
   * The one-click renderer that lives over the canvas in the ordinary editor.
   *
   * It is handed photo mode's store rather than one of its own, as a getter: photo mode is rebuilt
   * when storage changes hands at sign-in, and resolving per call means a render kept afterwards
   * lands in the account rather than in the local store that existed at construction.
   *
   * The site name is resolved the same way, and for a second reason besides renames: the project
   * menu is built *after* this widget, so there is nothing to read at construction time anyway.
   */
  private createQuickRender(): QuickRender {
    return new QuickRender({
      capture: this.capture,
      apiKeys: this.apiKeys,
      store: () => this.photoMode?.store ?? null,
      siteName: () => this.projectMenu?.documentName ?? DEFAULT_PROJECT_NAME,
      tags: () => this.tags.all(),
    });
  }

  /** Enter or leave photo mode. Called by the toolbar button and the `C` shortcut. */
  togglePhotoMode(force?: boolean): void {
    const photo = this.photoMode;
    if (!photo) return;
    const next = force ?? !photo.isActive;
    if (next) photo.enter();
    else photo.exit();
    this.hud?.refresh();
  }

  /**
   * Open the shots browser over the designer. Called by the toolbar's Shots button.
   *
   * Deliberately *not* photo mode any more. Going through saved frames and composing a new one are
   * different jobs: the first wants the whole window and the site still behind it, the second wants
   * the editor gone. Entering photo mode to browse also made re-staging incoherent — there is no
   * designer to be put back into while you are standing in the viewfinder. Photo mode's own drawer
   * is unchanged and still reached from its bar.
   */
  openShots(): void {
    this.shotsPanel?.toggle();
    this.hud?.refresh();
  }

  /**
   * Put a saved frame back onto the live site: the camera, and the environment it was taken under.
   *
   * The same restore photo mode's recall performs — one method, so a shot cannot come back one way
   * from the drawer and another way from the browser. What differs is where it leaves you, and that
   * is the caller's business: this returns you to the designer, standing where the shutter fired.
   *
   * Only ever reached for a shot whose site id matches the open document; the browser will not offer
   * the button otherwise. See `ShotsPanel.restageVerdict`.
   */
  private restageShot(shot: StoredShot): void {
    this.recallShot(shot);
    this.togglePhotoMode(false);
  }

  /** The open document, as the shots browser needs it: null id for a draft never yet written. */
  private siteIdentity(): { id: string; name: string } | null {
    const id = this.projectMenu?.documentId ?? null;
    if (id === null) return null;
    return { id, name: this.projectMenu?.documentName ?? DEFAULT_PROJECT_NAME };
  }

  /**
   * Hide everything that belongs to the editor rather than the garden: the HUD, the on-canvas
   * readout, and the overlay root (grid, north arrow, selection halos, placement ghost).
   *
   * The capture passes hide `overlayRoot` themselves, but doing it here too means what the user
   * composes against is exactly what comes back.
   */
  private setEditorChromeVisible(visible: boolean): void {
    this.hud?.setVisible(visible);
    if (this.readout) this.readout.style.display = visible ? '' : 'none';
    this.scene.overlayRoot.visible = visible;
    // The overlay root takes the lens pools with it, but the glow is on the plants' own materials and
    // would otherwise walk into a photo-mode shot and into the frame an AI render is conditioned on.
    // A neon garden is not what anyone composed, so the tint comes off with the rest of the chrome.
    this.lensRenderer?.setSuppressed(!visible);
  }

  /* ------------------------------------ Site lenses ------------------------------------ */

  /**
   * Repaint the lens.
   *
   * Called from everything that can change a reading: the lens or its mode being picked, the date
   * moving, the site being localized, and any edit at all — the last because `FloraWorld.update`
   * rebuilds a view when a plant's maturity changes, and a rebuilt view arrives with fresh materials
   * carrying no tint.
   */
  private applyLens(): void {
    const renderer = this.lensRenderer;
    if (!renderer) return;

    const lens = this.lensBar?.lens ?? null;
    const editor = this.floraEditor;
    this.lensReadings.clear();

    if (!lens || !editor) {
      renderer.clear();
      this.lensBar?.refresh();
      return;
    }

    const dayOfYear = this.environment.effectiveDayOfYear();
    const peak = this.lensBar?.peak === true;
    const targets: LensTarget[] = [];

    for (const instance of editor.world.all()) {
      const reading = readLens(lens, editor.world.resolved(instance), dayOfYear, { peak });
      this.lensReadings.set(instance.id, reading);
      // A hidden plant is not in the garden as far as the eye is concerned, and lighting one would
      // put heat on the ground under nothing.
      if (instance.hidden) continue;
      targets.push({
        id: instance.id,
        value: reading.value,
        described: reading.described,
        spreadMeters: editor.world.growthState(instance).spreadMeters,
        position: instance.position,
      });
    }

    renderer.update(targets);
    this.lensBar?.refresh();
  }

  /** One plant's reading, for the plan map. Computed against the map's own lens, not the bar's. */
  private lensReadingFor(instanceId: string, lens: LensDefinition): LensReading {
    const editor = this.floraEditor;
    const instance = editor?.world.get(instanceId);
    if (!editor || !instance) return UNREADABLE_LENS;
    return readLens(lens, editor.world.resolved(instance), this.environment.effectiveDayOfYear(), {
      peak: this.lensBar?.peak === true,
    });
  }

  /** What the bar's caption reports — read off the pass `applyLens` already made. */
  private lensSummary(): LensSummary | null {
    if (!this.lensBar?.lens) return null;
    return summarizeLens([...this.lensReadings.values()]);
  }

  /**
   * Return to a saved shot: camera *and* the environment it was taken under. Restoring only the
   * camera would reframe an autumn shot in whatever season is current, which is not "recall".
   */
  private recallShot(shot: StoredShot): void {
    this.capture.restoreCamera(shot.camera);
    // The restore may have put the camera on the lens the shot was taken with, which is the one
    // case where the row is not the thing that moved it.
    this.lensControl?.sync();
    this.environment.load(deserializeEnvironmentState(shot.environment));
    this.hud?.requestRefresh();
  }

  /**
   * Build a terrain from generation params, replacing any existing terrain, and frame it.
   *
   * `state` carries the parts of a saved site the parameters cannot reproduce — hand-graded heights
   * and painted ground. Without it a sculpted site would silently regenerate to its procedural shape
   * on reload, which is the one thing a save is for.
   */
  loadTerrain(
    params: TerrainGenerationParams,
    units: UnitSystem = 'imperial',
    state?: TerrainProjectState,
  ): void {
    this.clearTerrain();
    const terrain = new Terrain(params, units, {
      ...(state?.bakedHeights ? { bakedHeights: state.bakedHeights } : {}),
      // Sized for the palette in force as well as for what the file stored: a site whose ground
      // uses nine materials needs three splat pages before a single texel is decoded into it.
      surface: deserializeSurfaceMap(
        state?.surface,
        params.widthMeters,
        params.lengthMeters,
        undefined,
        surfacePalette.pages,
      ),
    });
    this.terrain = terrain;
    // New ground starts bare, so whatever winter the session is already in has to be laid over it —
    // the manager only *notifies* on a change, and opening a site in January is not one.
    terrain.setWinterCover(this.environment.groundCover());
    this.scene.terrainRoot.add(terrain.mesh);
    // New ground means every building sits at a new height, so views rebuild against this terrain.
    this.siteObjects.setGroundSampler((x, z) => terrain.sampleHeight(x, z));
    this.fauna.setGroundSampler((x, z) => terrain.sampleHeight(x, z));

    // The grid drapes over the ground, so it is fitted to this terrain and given its sampler; its
    // on/off state belongs to the session and is left alone here.
    const sampleHeight = (x: number, z: number): number => terrain.sampleHeight(x, z);
    if (this.gridOverlay) {
      this.gridOverlay.setSite(terrain.widthMeters, terrain.lengthMeters, { sampleHeight });
    } else {
      this.gridOverlay = new GridOverlay(terrain.widthMeters, terrain.lengthMeters, { sampleHeight });
      this.scene.overlayRoot.add(this.gridOverlay.object);
    }

    // The topo map reads the heightfield itself rather than sampling it, so it is handed the field
    // and the mesh's north rotation; everything else about it belongs to the session.
    if (!this.contourOverlay) {
      // Seeded from the site's own units; the HUD's display preference takes over from there.
      this.contourOverlay = new ContourOverlay({ unit: units });
      this.scene.overlayRoot.add(this.contourOverlay.object);
    }
    this.contourOverlay.setSite(terrain.field, terrain.mesh.rotation.y);

    this.northIndicator = createNorthIndicator();
    this.northIndicator.position.set(
      -terrain.widthMeters / 2 + 3,
      0,
      -terrain.lengthMeters / 2 + 3,
    );
    this.scene.overlayRoot.add(this.northIndicator);

    // Frame the camera to fit the site.
    const span = Math.max(terrain.widthMeters, terrain.lengthMeters);
    this.camera.focusOn(new THREE.Vector3(0, 0, 0), span * 1.1);
  }

  /** Dispose the current terrain and its overlays. */
  clearTerrain(): void {
    if (this.terrain) {
      this.scene.terrainRoot.remove(this.terrain.mesh);
      this.terrain.dispose();
      this.terrain = null;
    }
    // The grid deliberately outlives this: `loadTerrain` re-fits it, and disposing it here would
    // make every project open silently clear the designer's grid setting.
    if (this.northIndicator) {
      this.scene.overlayRoot.remove(this.northIndicator);
      disposeHelper(this.northIndicator);
      this.northIndicator = null;
    }
  }

  private aspect(): number {
    const { width, height } = this.size();
    return width / Math.max(1, height);
  }
  private size(): { width: number; height: number } {
    const width = this.container.clientWidth || 1;
    const height = this.container.clientHeight || 1;
    return { width, height };
  }

  private now(): number {
    return typeof performance !== 'undefined' ? performance.now() : Date.now();
  }

  /** Create a small on-canvas readout element for placement/selection feedback. */
  private createReadout(): HTMLElement {
    const el = document.createElement('div');
    el.className = 'lg-readout';
    this.container.appendChild(el);
    this.readout = el;
    return el;
  }

  /** Stop the loop and dispose all owned resources. */
  dispose(): void {
    this.events.emit('app:before-teardown', undefined);
    this.running = false;
    if (this.rafId !== null && typeof cancelAnimationFrame !== 'undefined') {
      cancelAnimationFrame(this.rafId);
    }
    this.rafId = null;
    if (typeof window !== 'undefined') {
      window.removeEventListener('keydown', this.onKeyDown);
    }
    for (const off of this.unsubscribe) off();
    this.unsubscribe.length = 0;
    this.resizeObserver?.disconnect();
    this.cameraController?.dispose();
    this.projectMenu?.dispose();
    this.projectMenu = null;
    this.lensControl?.dispose();
    this.lensControl = null;
    this.detailToggle?.dispose();
    this.detailToggle = null;
    this.quickRender?.dispose();
    this.quickRender = null;
    this.plantManifest?.dispose();
    this.plantManifest = null;
    this.tagIndex?.dispose();
    this.tagIndex = null;
    this.lensBar?.dispose();
    this.lensBar = null;
    // Disposes its own geometry, material and texture, and takes the glow back off the plants it put
    // it on — which matters, because the flora views outlive this renderer.
    this.lensRenderer?.dispose();
    this.lensRenderer = null;
    this.lensReadings.clear();
    this.photoMode?.dispose();
    this.photoMode = null;
    this.shotsPanel?.dispose();
    this.shotsPanel = null;
    this.wandBubble?.dispose();
    this.wandBubble = null;
    this.wandTool?.dispose();
    this.wandTool = null;
    this.tagBubble?.dispose();
    this.tagBubble = null;
    this.tagTool?.dispose();
    this.tagTool = null;
    this.tags.dispose();
    this.textureStudio?.dispose();
    this.textureStudio = null;
    // Before the HUD it is mounted in, and explicitly: it now outlives every storage rebuild, so
    // teardown is the only thing left that ends its subscription to the session.
    this.accountMenu?.dispose();
    this.accountMenu = null;
    // Its own layer on <body>, so the HUD's removal does not take it with it.
    this.tour.dispose();
    this.hud?.dispose();
    this.hud = null;
    this.sky.dispose();
    this.terrainEditor?.dispose();
    this.terrainEditor = null;
    this.floraEditor?.dispose();
    this.floraEditor = null;
    this.siteObjects.dispose();
    this.fauna.dispose();
    this.faunaTool?.dispose();
    this.faunaTool = null;
    this.objectTool?.dispose();
    this.objectTool = null;
    this.ghostFactory.dispose();
    this.gridOverlay?.dispose();
    this.gridOverlay = null;
    this.contourOverlay?.dispose();
    this.contourOverlay = null;
    if (this.readout) {
      this.readout.remove();
      this.readout = null;
    }
    this.clearTerrain();
    this.lighting.dispose();
    this.scene.dispose();
    this.renderer.dispose();
    this.events.clear();
  }
}

/**
 * The rule behind {@link Application.syncTopoToSculpt}, as a function of the three things it
 * depends on and nothing else.
 *
 * Pulled out because the interesting part is not the wiring but the bookkeeping: the overlay must be
 * restored exactly once, only if this transition is the one that raised it, and never over a choice
 * the designer made by hand. That is three states and four transitions, which is the size of thing
 * that goes quietly wrong inside a method that is also arbitrating tool exclusivity.
 *
 * @param sculpting Whether the sculpt tool now holds the canvas.
 * @param visible Whether the topo overlay is drawn right now.
 * @param remembered What it was set to before sculpt raised it, or null if sculpt did not raise it.
 */
export function topoSculptTransition(
  sculpting: boolean,
  visible: boolean,
  remembered: boolean | null,
): { visible: boolean; remembered: boolean | null } {
  if (sculpting) {
    // Already holding a memory means sculpt is already responsible for what is on screen; taking a
    // second snapshot here would record the overlay's *own* doing as the state to go back to.
    if (remembered !== null) return { visible, remembered };
    return { visible: true, remembered: visible };
  }
  if (remembered === null) return { visible, remembered };
  return { visible: remembered, remembered: null };
}
